import { useEffect, useRef, useState } from "react";
import { ArrowRight, FileText, Upload, X } from "lucide-react";
export function ImportDialog({
  onClose,
  onImport,
}: {
  onClose: () => void;
  onImport: (actions: unknown[]) => Promise<void>;
}) {
  const [tab, setTab] = useState<"pgn" | "fen">("pgn");
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [permitted, setPermitted] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    dialog.current?.showModal();
    return () => dialog.current?.close();
  }, []);
  async function file(file: File | undefined) {
    if (!file) return;
    if (file.size > 200000) {
      setError("Choose a PGN smaller than 200 KB.");
      return;
    }
    setText(await file.text());
    setTab("pgn");
    setError("");
  }
  return (
    <dialog
      ref={dialog}
      className="import-dialog"
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === dialog.current) onClose();
      }}
    >
      <div className="modal-header">
        <span>Import</span>
        <button
          className="icon-button"
          aria-label="Close import"
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </div>
      <h2>Import a game or position</h2>
      <p>Import a completed game or a position for permitted training.</p>
      <div className="segmented">
        <button
          className={tab === "pgn" ? "active" : ""}
          onClick={() => {
            setTab("pgn");
            setText("");
          }}
        >
          Game · PGN
        </button>
        <button
          className={tab === "fen" ? "active" : ""}
          onClick={() => {
            setTab("fen");
            setText("");
          }}
        >
          Position · FEN
        </button>
      </div>
      {tab === "pgn" && (
        <>
          <input
            ref={input}
            type="file"
            accept=".pgn,.txt"
            hidden
            onChange={(e) => void file(e.target.files?.[0])}
          />
          <button
            className="upload-zone"
            onClick={() => input.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              void file(e.dataTransfer.files[0]);
            }}
          >
            <Upload size={22} />
            <span>Choose a PGN, or drop it here</span>
            <small>One game · up to 200 KB</small>
          </button>
        </>
      )}
      <textarea
        autoFocus
        aria-label={tab === "pgn" ? "PGN game text" : "FEN position"}
        placeholder={
          tab === "pgn" ? "Or paste your PGN here…" : "Paste a FEN position…"
        }
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={tab === "pgn" ? 5 : 3}
        maxLength={200000}
      />
      <label className="permission-check">
        <input
          type="checkbox"
          checked={permitted}
          onChange={(e) => setPermitted(e.target.checked)}
        />{" "}
        This is a completed game or a position where training assistance is
        permitted.
      </label>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <button
        className="button primary import-submit"
        disabled={!text.trim() || !permitted || busy}
        onClick={async () => {
          setBusy(true);
          setError("");
          try {
            await onImport([
              tab === "pgn"
                ? { type: "game.import", pgn: text.trim() }
                : { type: "game.create", fen: text.trim() },
            ]);
            onClose();
          } catch (e) {
            setError(
              e instanceof Error
                ? e.message
                : "The import could not be completed.",
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy
          ? "Validating…"
          : tab === "pgn"
            ? "Open this game"
            : "Explore this position"}
        <ArrowRight size={16} />
      </button>
    </dialog>
  );
}
